/* eslint-disable */
import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import * as XLSX from "xlsx";

import { useTranslation } from "react-i18next";
import * as url from "urlhelpers/UrlHelper";
import { getData } from "utils/Gateway";
import { formatUtcDate } from "helpers/DateCalculation";
import { Link } from "react-router-dom";
import { GlobalProvider } from "context/GlobalContext";

import ActionLogHeader from "./Header/ActionLogHeader";
import ActionLogBreadCrumb from "./BreadCrumb/ActionLogBreadCrumb";
import ActionLogFilter from "./Filter/ActionLogFilter";
import ActionLogDetailsOffcanvas from "./Details/ActionLogDetailsOffcanvas";

// Tracked actions info shown above the table
const TRACKED_ACTIONS = ["create", "update", "delete", "change educator"];

// Column id → query param name
const FIELD_TO_PARAM = {
  fullname: "fullname",
  email: "email",
  module: "module",
  action: "action",
  ipaddress: "ipaddress",
  url: "actionurl",
  systeminfo: "systeminfo",
  actiondateformated: "actiondateformated",
  entitycustomerid: "entitycustomerid",
};

/** Build a human-readable label for the active date filter */
const buildDateLabel = (filterDates, todayValue) => {
  if (!filterDates || filterDates === "") return `Today (${todayValue})`;
  if (filterDates.includes("|")) {
    const [from, to] = filterDates.split("|");
    return `${from} → ${to}`;
  }
  if (filterDates.includes(",")) {
    return filterDates.split(",").join(", ");
  }
  return filterDates;
};

const ActionLogList = () => {
  const {
    reloadActionLogList,
    setreloadActionLogList,
    screenHeight,
    filterActionLogDates,
    setfilterActionLogDates,
    filterActionLogParams,
    isActionLogsLoading,
    setIsActionLogsLoading,
  } = useContext(GlobalProvider);

  const todayValue = new Date().toISOString().split("T")[0];
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  const [actionLogs, setActionLogs] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [responseReceived, setResponseReceived] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterTimer = useRef(null);

  const [downloadExcel, setDownloadExcel] = useState(false);
  const [records, setRecords] = useState(0);
  const [search, setSearch] = useState("");
  const blockRefSelected = useRef(null);

  const checkedRecordCount = (selectCount) => {
    if (blockRefSelected.current) {
      blockRefSelected.current.innerHTML = selectCount;
    }
  };

  const getSelectedData = () => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    return actionLogs.filter((item) => selectedIds.includes(item._id));
  };

  const exportExcel = () => {
    const selectedData = getSelectedData();
    const exportData = selectedData.length > 0 ? selectedData : actionLogs;
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "ActionLogs.xlsx");
  };

  const tableColumns = useMemo(
    () => [
      {
        id: "fullname",
        accessorKey: "fullname",
        header: t("User"),
        size: 280,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span>{row.original.fullname}</span>
            <button
              className="bg-transparent p-0 border-0"
              type="button"
              data-bs-toggle="offcanvas"
              data-bs-target="#actionLogDetailsOffcanvas"
              onClick={() => setSelectedLogId(row.original._id)}
            >
              <span className="material-symbols-outlined">more_vert</span>
            </button>
          </div>
        ),
      },
      {
        id: "email",
        accessorKey: "email",
        header: t("Email"),
        size: 280,
      },
      {
        id: "module",
        accessorKey: "module",
        header: t("Module"),
        size: 200,
      },
      {
        id: "action",
        accessorKey: "action",
        header: t("Action"),
        size: 180,
      },
      {
        id: "entitycustomerid",
        accessorKey: "entitycustomerid",
        header: t("Customer ID"),
        size: 180,
      },
      {
        id: "ipaddress",
        accessorKey: "ipaddress",
        header: t("Ip Address"),
        size: 220,
      },
      {
        id: "url",
        accessorKey: "url",
        header: t("Url"),
        size: 260,
        Cell: ({ cell }) => {
          const raw = cell.getValue() ?? "";
          // strip query string — show only the path
          const path = raw.split("?")[0];
          return (
            <span title={raw} className="text-truncate d-block" style={{ maxWidth: "240px" }}>
              {path}
            </span>
          );
        },
      },
      {
        id: "systeminfo",
        accessorKey: "systeminfo",
        header: t("System Information"),
        size: 400,
      },
      {
        id: "actiondateformated",
        accessorKey: "actiondateformated",
        header: t("Date"),
        size: 180,
      },
    ],
    [t]
  );

  const getAllActionLogs = async (currentColumnFilters = columnFilters) => {
    try {
      setIsActionLogsLoading(true);
      let requestUrl =
        url.API_BASEURL + url.API_GET_ACTION_LOGS + `?token=${token}`;

      // Date filter
      const activeDates = filterActionLogDates && filterActionLogDates !== ""
        ? filterActionLogDates
        : todayValue;
      requestUrl += `&filterdates=${activeDates}`;

      // Extra filters from the filter panel
      if (filterActionLogParams) {
        const { module, action, entitycustomerid, fullname, email } = filterActionLogParams;
        if (module)           requestUrl += `&module=${encodeURIComponent(module)}`;
        if (action)           requestUrl += `&action=${encodeURIComponent(action)}`;
        if (entitycustomerid) requestUrl += `&entitycustomerid=${encodeURIComponent(entitycustomerid)}`;
        if (fullname)         requestUrl += `&fullname=${encodeURIComponent(fullname)}`;
        if (email)            requestUrl += `&email=${encodeURIComponent(email)}`;
      }

      // Column-level filters from the table header
      currentColumnFilters.forEach(({ id, value }) => {
        const param = FIELD_TO_PARAM[id];
        if (param && value?.toString().trim()) {
          requestUrl += `&${param}=${encodeURIComponent(value.toString().trim())}`;
        }
      });

      const response = await getData(requestUrl);

      if (response.data) {
        setActionLogs(response.data);
        setResponseReceived(true);
        setRecords(response.data.length);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsActionLogsLoading(false);
    }
  };

  useEffect(() => {
    downloadExcel && exportExcel();
  }, [downloadExcel]);

  useEffect(() => {
    if (responseReceived === false) {
      getAllActionLogs();
    }
  }, [responseReceived]);

  useEffect(() => {
    if (reloadActionLogList) {
      getAllActionLogs();
      setreloadActionLogList(false);
    }
  }, [reloadActionLogList]);

  useEffect(() => {
    getAllActionLogs();
  }, [filterActionLogDates, filterActionLogParams]);

  useEffect(() => {
    setRecords(actionLogs.length);
  }, [actionLogs]);

  useEffect(() => {
    clearTimeout(columnFilterTimer.current);
    columnFilterTimer.current = setTimeout(() => {
      getAllActionLogs(columnFilters);
    }, 500);
  }, [columnFilters]);

  useEffect(() => {
    const selectedCount = Object.keys(rowSelection).filter((key) => rowSelection[key]).length;
    checkedRecordCount(selectedCount);
  }, [rowSelection, actionLogs]);

  const activeDateLabel = buildDateLabel(filterActionLogDates, todayValue);

  return (
    <div className="tab-content" id="surveyTabContent">
      <div className="container-fluid px-4">
        <ActionLogBreadCrumb />
        <ActionLogHeader
          setSearch={setSearch}
          setDownloadExcel={setDownloadExcel}
          records={records}
          blockRefSelected={blockRefSelected}
        />

        <ActionLogFilter />

        {/* ── Active filter summary ─────────────────────────────────────── */}
        <div className="d-flex flex-wrap align-items-center gap-2 my-3">
          {/* Active date */}
          <span className="d-flex align-items-center gap-1 badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 fw-normal px-2 py-1">
            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>calendar_today</span>
            {activeDateLabel}
          </span>

          {/* Active extra filters */}
          {filterActionLogParams?.module && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 fw-normal px-2 py-1">
              {t("Module")}: {filterActionLogParams.module}
            </span>
          )}
          {filterActionLogParams?.action && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 fw-normal px-2 py-1">
              {t("Action")}: {filterActionLogParams.action}
            </span>
          )}
          {filterActionLogParams?.entitycustomerid && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 fw-normal px-2 py-1">
              {t("Customer ID")}: {filterActionLogParams.entitycustomerid}
            </span>
          )}
          {filterActionLogParams?.fullname && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 fw-normal px-2 py-1">
              {t("User")}: {filterActionLogParams.fullname}
            </span>
          )}
          {filterActionLogParams?.email && (
            <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 fw-normal px-2 py-1">
              {t("Email")}: {filterActionLogParams.email}
            </span>
          )}

          {/* Tracked actions info */}
          <span className="ms-auto d-flex align-items-center gap-1 text-muted small">
            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>info</span>
            {t("Tracked actions")}:
            {TRACKED_ACTIONS.map((a) => (
              <span key={a} className="badge bg-light text-dark border fw-normal">{a}</span>
            ))}
          </span>
        </div>
        {/* ── End active filter summary ─────────────────────────────────── */}

        <ActionLogDetailsOffcanvas
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />

        <div className="table-responsive">
          <MaterialReactTable
            columns={tableColumns}
            data={actionLogs}
            enableSorting
            enableFilters
            enableGlobalFilter={false}
            manualFiltering
            enableGrouping
            enableColumnOrdering
            enableStickyHeader
            enableRowSelection
            onRowSelectionChange={setRowSelection}
            onColumnFiltersChange={setColumnFilters}
            getRowId={(row) => row._id}
            state={{ rowSelection, columnFilters, isLoading: isActionLogsLoading }}
            muiCircularProgressProps={{ style: { display: "none" } }}
            muiTableContainerProps={{ sx: { maxHeight: screenHeight } }}
            muiTableBodyRowProps={{ sx: { overflow: "visible" } }}
            muiTableBodyProps={{ sx: { overflow: "visible" } }}
            initialState={{
              pagination: { pageSize: 100, pageIndex: 0 },
              showColumnFilters: true,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ActionLogList;
